import React, { useEffect, useState } from 'react';
import {
    Card, CardContent, CardHeader, CardTitle, CardDescription
} from '@/components/ui/card';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Eye, Package, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderTab = ({ restaurantId }) => {

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    // 🔥 NEW STATES
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const token = localStorage.getItem("token");

    useEffect(() => {
        if (restaurantId) {
            fetchOrders();
        }
    }, [restaurantId]);

    const fetchOrders = async () => {
        try {
            const response = await axios.get(
                `${API}/restaurant-profile/${restaurantId}/get_orders`);

            setOrders(response.data || []);
        } catch (error) {
            console.error("API Error:", error?.response?.data || error.message);
            toast.error("Failed to fetch Orders");
        } finally {
            setLoading(false);
        }
    };

    // 🔥 FETCH ORDER DETAILS
    const fetchOrderDetails = async (orderId) => {
        try {
            setDetailsLoading(true);

            const res = await axios.get(`${API}/restaurant-profile/${restaurantId}/${orderId}`);

            setOrderDetails(res.data);
            setSelectedOrder(orderId);

        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch order details");
        } finally {
            setDetailsLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            PENDING: "bg-amber-100 text-amber-700 border-amber-200",
            ACCEPTED: "bg-blue-100 text-blue-700 border-blue-200",
            PREPARING: "bg-indigo-100 text-indigo-700 border-indigo-200",
            OUT_FOR_DELIVERY: "bg-purple-100 text-purple-700 border-purple-200",
            COMPLETED: "bg-green-100 text-green-700 border-green-200",
            CANCELLED: "bg-red-100 text-red-700 border-red-200",
        };
        return colors[status] || "bg-slate-100 text-slate-700";
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                <p className="text-sm text-muted-foreground">Loading orders...</p>
            </div>
        );
    }

    return (
        <>
            <Card className="border-none shadow-none bg-transparent">
                <CardHeader className="px-0 pt-0 flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-xl">Orders</CardTitle>
                        <CardDescription>Manage incoming and past orders</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchOrders}>
                        Refresh
                    </Button>
                </CardHeader>

                <CardContent className="px-0">
                    <div className="rounded-lg border bg-white overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50">
                                    <TableHead>Order ID</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Customer</TableHead>
                                    <TableHead>Method</TableHead>
                                    <TableHead>Total</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                            </TableHeader>

                            <TableBody>
                                {orders.length > 0 ? (
                                    orders.map((order) => (
                                        <TableRow key={order._id}>
                                            <TableCell className="font-bold text-primary">
                                                #{order.orderNumber}
                                            </TableCell>

                                            <TableCell>
                                                <div className="text-xs">
                                                    {format(new Date(order.createdAt), 'dd MMM, yyyy')}
                                                    <br />
                                                    {format(new Date(order.createdAt), 'hh:mm a')}
                                                </div>
                                            </TableCell>

                                            <TableCell>
                                                {order.user?.name || 'Guest'}
                                            </TableCell>

                                            <TableCell>
                                                <Badge variant="outline">
                                                    {order.paymentMethod}
                                                </Badge>
                                            </TableCell>

                                            <TableCell>
                                                ₹{order.grandTotal.toFixed(2)}
                                            </TableCell>

                                            <TableCell>
                                                <Badge className={getStatusColor(order.status)}>
                                                    {order.status}
                                                </Badge>
                                            </TableCell>

                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => fetchOrderDetails(order._id)}
                                                >
                                                    <Eye size={16} />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-10">
                                            No orders found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* 🔥 MODAL */}
            {selectedOrder && (
                <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
                    <div className="bg-white w-[600px] max-h-[80vh] overflow-y-auto rounded-lg p-5">

                        {detailsLoading ? (
                            <Loader2 className="animate-spin mx-auto" />
                        ) : (
                            <>
                                <h2 className="text-lg font-bold mb-3">
                                    Order #{orderDetails?.order?.orderNumber}
                                </h2>

                                <p className="text-sm mb-3">
                                    {orderDetails?.order?.user?.name}
                                </p>
                                <p className="text-sm mb-3">
                                    {orderDetails?.order?.user?.phone}
                                </p>

                                {/* 🔥 ITEMS */}
                                {orderDetails?.order?.items?.length > 0 ? (
                                    orderDetails.order.items.map((item, index) => (
                                        <div key={index} className="border p-2 mb-2 rounded">
                                            <div className="flex justify-between font-medium">
                                                <span>{item.name} × {item.quantity}</span>
                                                <span>₹{item.totalPrice}</span>
                                            </div>

                                            {/* ADDONS */}
                                            {item.addons?.length > 0 && (
                                                <div className="ml-3 text-xs text-gray-500">
                                                    {item.addons.map((addon, i) => (
                                                        <div key={i}>
                                                            + {addon.title} (₹{addon.price})
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-6 text-gray-500">
                                        ⚠️ No items found
                                    </div>
                                )}

                                {/* 🔥 BILL SUMMARY */}
                                <div className="border-t pt-3 mt-3 text-sm space-y-1">
                                    <div className="flex justify-between">
                                        <span>Items Total</span>
                                        <span>₹{orderDetails?.order?.billSummary?.itemTotal}</span>
                                    </div>

                                    <div className="flex justify-between">
                                        <span>Tax</span>
                                        <span>₹{orderDetails?.order?.billSummary?.taxAmount}</span>
                                    </div>

                                    <div className="flex justify-between">
                                        <span>Delivery Fee</span>
                                        <span>₹{orderDetails?.order?.billSummary?.deliveryFee}</span>
                                    </div>

                                    {Number(orderDetails?.order?.billSummary?.platformFee) > 0 && (
                                        <div className="flex justify-between">
                                            <span>Platform Fee</span>
                                            <span>₹{orderDetails.order.billSummary.platformFee}</span>
                                        </div>
                                    )}

                                    {Number(orderDetails?.order?.billSummary?.packingCharge) > 0 && (
                                        <div className="flex justify-between">
                                            <span>Packing Charge</span>
                                            <span>₹{orderDetails.order.billSummary.packingCharge}</span>
                                        </div>
                                    )}

                                    {Number(orderDetails?.order?.billSummary?.rainFee) > 0 && (
                                        <div className="flex justify-between">
                                            <span>Rain Fee</span>
                                            <span>₹{orderDetails.order.billSummary.rainFee}</span>
                                        </div>
                                    )}

                                    {Number(orderDetails?.order?.billSummary?.additionalChargesTotal) > 0 && (
                                        <div className="flex justify-between">
                                            <span>Additional Charges</span>
                                            <span>₹{orderDetails.order.billSummary.additionalChargesTotal}</span>
                                        </div>
                                    )}

                                    <div className="flex justify-between">
                                        <span>Discount</span>
                                        <span>-₹{orderDetails?.order?.billSummary?.discount}</span>
                                    </div>

                                    {Number(orderDetails?.order?.billSummary?.roundOff) !== 0 && (
                                        <div className="flex justify-between">
                                            <span>Round Off</span>
                                            <span>{Number(orderDetails.order.billSummary.roundOff) > 0 ? '+' : '-'}₹{Math.abs(Number(orderDetails.order.billSummary.roundOff)).toFixed(2)}</span>
                                        </div>
                                    )}

                                    <div className="flex justify-between font-bold text-lg">
                                        <span>Total</span>
                                        <span>₹{orderDetails?.order?.billSummary?.grandTotal}</span>
                                    </div>
                                </div>

                                <Button
                                    className="mt-4 w-full"
                                    onClick={() => setSelectedOrder(null)}
                                >
                                    Close
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default OrderTab;